import { format } from "date-fns"
import { es } from "date-fns/locale"
import { TFunction } from "react-i18next"
import { getStatus } from "../../../../hooks/status"
import { History } from "../../../../service/dashboard/response"
import { Constants } from "../../../../utils/constants"
import { intervaleList } from "../../../../utils/constants/intervaleList"
import { Status } from "../../../../utils/enums"
import { buildUTCOffsetDateRange, fmtDateTimeWithTz } from "../../../../utils/methods/calcDate"
import { deleteDecimalsWithZero } from "../../../../utils/methods/removeDecimals"
import { ToDate } from "../../../../utils/methods/toDate"
import { Color } from "../../../../utils/theme/types/color"
import { calc30DaysAgo } from "../../../dashboard/pages/main/fragments/indicators/methods/callEndpoints"
import { FilterData } from "./typing/interfaces"
import { TableData } from "./typing/type"

const UnitDataFilters = (payload?: TableData) => {
    let filters = []
    if (!!payload?.questionnaire?.length) {
        filters.push({ terms: { cuestionario_id: payload?.questionnaire } })
    }
    if (!!payload?.questionnaireStatus?.length) {
        let newStatusList: (Status | string)[] = payload?.questionnaireStatus.map((item) => item)
        if (newStatusList.some(item => item == Status.WAITING)) {
            newStatusList = [...newStatusList, Status.ACTIVE, Status.PAUSE, Status.REAGENDED, Status.STARTED]
        }
        filters.push({ terms: { estatus_id: newStatusList } })
    }

    if (!!payload?.percentage?.length) {
        filters.push({
            range: {
                avance: {
                    gte: payload?.percentage[0],
                    lte: payload?.percentage[1],
                },
            },
        })
    }

    if (!!payload?.area?.length) {
        filters.push({ terms: { area_id: payload?.area } })
    }
    if (!!payload?.user?.length) {
        filters.push({ terms: { usuario_id: payload?.user } })
    }
    if (!!payload?.department?.length) {
        filters.push({ terms: { departamento_id: payload?.department } })
    }
    if (!!payload?.unit?.length) {
        filters.push({ terms: { unidad_id: payload?.unit } })
    }
    if (!!payload?.division?.length) {
        filters.push({ terms: { division_unidad_id: payload?.division } })
    }
    if (!!payload?.to && !!payload?.from) {
        const { gte, lte } = buildUTCOffsetDateRange({
            from: payload.from,
            to: payload.to,
            intervale: payload.intervale,
        })

        filters.push({
            range: {
                [payload.intervale == '1w' ? 'fecha_inicio_semana' : 'fecha_inicio']: {
                    gte: payload?.from ? gte : undefined,
                    lte: payload?.to ? lte : undefined,
                },
            },
        })
    }

    return { filters }
}

const formatResponsUnitData = (
    response: History,
    colors: Color,
    t: TFunction<"translation", undefined>,
    intervale?: string
) => {

    const fmtDate = (value: string | Date): string => {
        if (!value) return ''
        return format(
            intervale === '1w' ? new Date(value) : ToDate(value),
            Constants.humanFormatShort2,
            { locale: es },
        )
    }

    const fmtDateTime = (value: string, timezone: string) => fmtDateTimeWithTz(value, timezone, `${Constants.dayjsHumanFormatShort2} hh:mm A`)

    const hits =
        response?.hits?.hits?.map((item) => {
            const source = item?._source
            const statusQuestionnaire = getStatus(source.estatus_id, colors, t)
            const timezone = source.zona_horaria ?? Constants.defaultTimeZone

            return {
                id: item?._id,
                area: source.area ?? "-",
                area_id: source.area_id,
                fecha: fmtDate(source.fecha),
                fecha_inicio: fmtDate(source.fecha_inicio),
                semana: source.semana,
                departamento: source.departamento ?? "-",
                fecha_date: source.fecha_inicio_semana,
                departamento_id: source.departamento_id,
                estatus_id: source.estatus_id,
                usuario: source.usuario,
                unidad: source.unidad,
                unidad_id: source.unidad_id,
                unidad_ceco: source.unidad_ceco,
                division_unidad: source.division_unidad,
                division_usuario: source.division_usuario,
                avance:
                    source?.avance || source?.avance == "0"
                        ? `${deleteDecimalsWithZero(Number(source?.avance).toFixed(2))}%`
                        : null,
                usuario_asignado: source.usuario_asignado,
                por_iniciar: source.por_iniciar,
                en_progreso: source.en_progreso,
                finalizado: source.finalizado,
                incompleto: source.incompleto,
                vencido: source.vencido,
                status:
                    statusQuestionnaire.value == Status.WAITING
                        ? "En progreso"
                        : statusQuestionnaire.text,
                cancelado: source.cancelado,
                cuestionario: source.cuestionario ?? "-",
                cuestionario_estatus_id: source.cuestionario_estatus_id ?? "-",
                cuestionario_estatus: source.cuestionario_estatus ?? "-",
                cuestionario_id: source.cuestionario_id,
                zona: source.zona_unidad ?? "-",
                tipo_usuario: source.tipo_usuario ?? "-",
                tipo_programacion: source.tipo_programacion ?? "-",
                fecha_inicio_programacion: fmtDateTime(source.fecha_inicio, timezone),
                fecha_fin_programacion: fmtDateTime(source.fecha_fin, timezone),
                fecha_inicio_respuesta: fmtDateTime(source.fecha_inicio_respuesta, timezone),
                fecha_fin_respuesta: fmtDateTime(source.fecha_fin_respuesta, timezone),

                resultado: source.resultado_agendamiento ? `${source.resultado_agendamiento}%` : "-",
            }
        }) ?? []

    return { hits }
}


const formatDataFiltered = ({ filterData }: { filterData: FilterData }) => {
    const {
        role,
        division,
        user,
        userType,
        questionnaire,
        month,
        createYear,
        area,
        department,
        zone,
        child,
        from,
        to,
        unidad_id,
        questionnaireStatus,
        percentage,
        intervale
    } = filterData
    const intervaleValue = intervale?.value ?? intervaleList[0].value
    const units = unidad_id?.map((item) => item.value)
    const roles = role?.map((item) => item.value)
    const divisions = division?.map((item) => item.value)
    const users = user?.map((item) => item.value)
    const userTypes = userType?.map((item) => item.value)
    const questionnaires = questionnaire?.map((item) => item.value)
    const questionnaireStatusIds = questionnaireStatus?.map((item) => item.value)
    const months = month?.map((item) => item.label)
    const years = createYear?.map((item) => item.value)
    const areas = area?.map((item) => item.value)
    const departments = department?.map((item) => item.value)
    const zones = [
        ...(zone?.map((item) => item.value) ?? []),
        ...(child?.map((item) => item.value) ?? []),
    ]
    let end, start

    if (!!to || !!from) {
        const { endDate, startDate } = calc30DaysAgo({
            start: from,
            end: to,
            format: Constants.dateFormat3
        })
        end = endDate
        start = startDate
    }


    return {
        data: {
            from: start,
            to: end,
            area: areas,
            user: users,
            department: departments,
            division: divisions,
            questionnaire: questionnaires,
            questionnaireStatus: questionnaireStatusIds,
            role: roles,
            unit: units,
            userType: userTypes,
            zone: zones,
            month: months,
            years,
            intervale: intervaleValue,
            percentage
        }
    }
}

export { formatDataFiltered, formatResponsUnitData, UnitDataFilters }

